import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { PageHeader } from "../components/PageHeader";
import { StatCard } from "../components/StatCard";
import { StepShell } from "../components/StepShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  levelClasses,
  useHealthSummary,
  type HealthSignal,
} from "../lib/api/health";
import { useModelRegistry } from "../lib/api/registry";

function Pill({
  text,
  level,
  size = "md",
}: {
  text: string;
  level: string;
  size?: "md" | "lg";
}) {
  const sizeCls =
    size === "lg"
      ? "px-3 py-1 text-sm tracking-wide"
      : "px-2 py-0.5 text-[10px] tracking-wide";
  return (
    <span
      className={`inline-flex items-center rounded-md border font-semibold uppercase ${sizeCls} ${levelClasses(level)}`}
    >
      {text}
    </span>
  );
}

type SignalLink = { to: string };

function signalLink(s: HealthSignal): SignalLink | null {
  if (s.label === "Asset Type") {
    return { to: "/model-drift-monitoring/input-stability?tab=psi&feature=asset_type&from=health" };
  }
  if (s.label === "Credit inputs") {
    return { to: "/model-drift-monitoring/input-stability?tab=psi&from=health" };
  }
  if (s.label === "Macro inputs") {
    return { to: "/model-drift-monitoring/input-stability?tab=csi&from=health" };
  }
  if (
    s.label.startsWith("Mature vintages") ||
    s.label.startsWith("Recent vintages") ||
    s.label.startsWith("Discrimination")
  ) {
    return { to: "/model-drift-monitoring/observability?from=health" };
  }
  return null;
}

function SignalRow({ s }: { s: HealthSignal }) {
  const link = signalLink(s);
  const body = (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{s.label}</div>
        <div className="text-xs text-muted-foreground">{s.detail}</div>
      </div>
      <Pill text={s.level.replace("_", " ")} level={s.level} />
    </div>
  );

  if (!link) {
    return (
      <div className="border-t border-border first:border-t-0">{body}</div>
    );
  }
  return (
    <Link
      to={link.to}
      className="block cursor-pointer border-t border-border px-2 -mx-2 rounded-md transition-colors first:border-t-0 hover:bg-amber-dim/20"
    >
      {body}
    </Link>
  );
}

export function HealthPage() {
  const { data: models } = useModelRegistry();
  const [modelId, setModelId] = useState<number>(1);

  const uniqueModels = models
    ? models.filter(
        (m, i, arr) =>
          arr.findIndex(
            (x) => x.modelName === m.modelName && x.modelVersion === m.modelVersion,
          ) === i,
      )
    : [];

  const { data, isLoading } = useHealthSummary(modelId);

  if (isLoading || !data) {
    return (
      <StepShell step={1} modelId={modelId} models={uniqueModels} onModelChange={setModelId}>
        <div className="h-64 animate-pulse rounded-md bg-muted/20" />
      </StepShell>
    );
  }

  const { model, drift, decay, summary, whatThisMeans } = data;

  return (
    <StepShell step={1} modelId={modelId} models={uniqueModels} onModelChange={setModelId}>
      <PageHeader
        title="Health"
        subtitle={'Two questions answer "is the model still trustworthy?" Is it drifting, and is it decaying.'}
      />

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card className="bg-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base font-semibold">
                Is the model drifting?
              </CardTitle>
              <Pill text={drift.verdict} level={drift.verdict} size="lg" />
            </div>
            <p className="pt-2 text-sm text-muted-foreground">
              {drift.headline}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              {drift.signals.map((s) => (
                <SignalRow key={s.label} s={s} />
              ))}
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
                <span>Asset Type PSI · {model.cohortsMonitored} cohorts</span>
                <span>threshold 0.25</span>
              </div>
              <div className="h-[70px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={drift.assetTypePsiTrend}
                    margin={{ top: 4, right: 4, left: 4, bottom: 0 }}
                  >
                    <XAxis dataKey="cohort" hide />
                    <YAxis hide domain={[0, "dataMax"]} />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 6,
                        fontSize: 11,
                      }}
                      labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      formatter={(v: number) => [v.toFixed(2), "PSI"]}
                    />
                    <ReferenceLine
                      y={0.25}
                      stroke="hsl(var(--muted-foreground))"
                      strokeDasharray="3 3"
                      strokeOpacity={0.5}
                    />
                    <Line
                      type="monotone"
                      dataKey="psi"
                      stroke="var(--amber)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base font-semibold">
                Is the model decaying?
              </CardTitle>
              <Pill text={decay.verdict} level={decay.verdict} size="lg" />
            </div>
            <p className="pt-2 text-sm text-muted-foreground">
              {decay.headline}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              {decay.signals.map((s) => (
                <SignalRow key={s.label} s={s} />
              ))}
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
                <span>Cumulative default % by months on book</span>
                <span>
                  <span className="text-amber">predicted</span> ·{" "}
                  <span className="text-[color:var(--approve)]">actual</span>
                </span>
              </div>
              <div className="h-[70px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={decay.seasoningSparkline}
                    margin={{ top: 4, right: 4, left: 4, bottom: 0 }}
                  >
                    <XAxis dataKey="mob" hide />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--popover))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 6,
                        fontSize: 11,
                      }}
                      labelFormatter={(l) => `${l} MOB`}
                      formatter={(v: number, n: string) => [
                        `${v.toFixed(1)}%`,
                        n,
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="predicted"
                      stroke="var(--amber)"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="actual"
                      stroke="var(--approve)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Overall Health"
          value={<Pill text={summary.overallHealth} level={summary.overallHealth} size="lg" />}
        />
        <Link
          to="/model-drift-monitoring/input-stability?tab=psi&from=health"
          className="block rounded-xl transition-colors hover:ring-1 hover:ring-amber/60"
        >
          <StatCard
            label="Open Critical"
            value={String(summary.openCritical)}
            accent="red"
            sublabel="View PSI →"
          />
        </Link>
        <Link
          to="/model-drift-monitoring/input-stability?tab=csi&from=health"
          className="block rounded-xl transition-colors hover:ring-1 hover:ring-amber/60"
        >
          <StatCard
            label="Open Warnings"
            value={String(summary.openWarn)}
            accent="amber"
            sublabel="View CSI →"
          />
        </Link>
        <StatCard
          label="Monitoring Run"
          value={model.lastRunCohort}
          sublabel={`${model.cohortsMonitored} cohorts`}
        />
      </div>

      <Card className="mt-6 bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-[0.18em] text-amber">
            What this means
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className=" text-sm leading-relaxed text-foreground/90">
            {whatThisMeans}
          </p>
        </CardContent>
      </Card>
    </StepShell>
  );
}
